import type { AiProviderCreationFailedAction } from '@Application/Ai/AiConfiguration/Providers/Actions/aiProviderCreationFailed';
import type { AiProviderUpdateFailedAction } from '@Application/Ai/AiConfiguration/Providers/Actions/aiProviderUpdateFailed';
import CreateAiProvider from '@Application/Ai/AiConfiguration/Providers/Commands/CreateAiProvider';
import UpdateAiProvider from '@Application/Ai/AiConfiguration/Providers/Commands/UpdateAiProvider';
import createAiProvider from '@Application/Ai/AiConfiguration/Providers/createAiProvider';
import updateAiProvider from '@Application/Ai/AiConfiguration/Providers/updateAiProvider';
import FetchAiProviderKinds from '@Application/Ai/AiProviderKinds/Queries/FetchAiProviderKinds';
import fetchAiProviderKinds from '@Application/Ai/AiProviderKinds/fetchAiProviderKinds';
import providerSidebarSignal, {
	closeProviderSidebar,
} from '@UI/Pages/Administration/AiEngines/Signals/providerSidebarSignal';
import AiProviderFormData from '@UI/Pages/Administration/AiEngines/Sidebars/ProviderSidebar/FormData/AiProviderFormData';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import * as React from 'react';
import { AZURE_SPEECH_KIND } from '@UI/Pages/Administration/AiEngines/azureSpeechValues';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector, useStoreQuery } from '@verclam/synapse';
import { Button, Dropdown, Form, InputText, Password, Sidebar, Switch } from '@verclam/ui-kit';

/** Matches `AiProviderKind.AZURE_OPENAI` (`apps/backend/src/enums/ai/ai-provider-kind.enum.ts`). */
const AZURE_OPENAI_KIND = 'azure_openai';

/**
 * One panel for both creation and edition, told apart by `providerId` on the signal's context. Reads
 * the edited provider straight from the store rather than through a prop: the page mounts this
 * component once, with no data of its own to pass it.
 */
const ProviderSidebar: React.FC = () => {
	const dispatch = useDispatch();
	const context = useSignalValue(providerSidebarSignal);
	const providers = useSelector((state) => state.aiConfigurationStore.providers);
	const { user } = useStrictAuthenticatedUser();

	const [formData, setFormData] = React.useState(() => AiProviderFormData.createEmpty());
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const { data: providerKinds } = useStoreQuery({
		selector: (state) => state.aiProviderKindsStore.aiProviderKinds,
		stopOn: (value) => value !== null,
		query: () => dispatch(fetchAiProviderKinds(new FetchAiProviderKinds())),
	});

	const formId = React.useId();
	const providerId = context?.providerId ?? null;

	const editedProvider = React.useMemo(() => {
		if (providerId === null || !providers) {
			return null;
		}

		return providers.findById(providerId);
	}, [providerId, providers]);

	React.useEffect(() => {
		if (context === null) {
			return;
		}

		setFormData(
			editedProvider !== null
				? AiProviderFormData.fromView(editedProvider)
				: AiProviderFormData.createEmpty(),
		);
	}, [context, editedProvider]);

	const handleLabelChange = React.useCallback((value: string) => {
		setFormData((current) => current.setLabel(value));
	}, []);

	const handleKindChange = React.useCallback((value: string | null) => {
		setFormData((current) => current.setKind(value ?? ''));
	}, []);

	const handleBaseUrlChange = React.useCallback((value: string) => {
		setFormData((current) => current.setBaseUrl(value));
	}, []);

	const handleApiVersionChange = React.useCallback((value: string) => {
		setFormData((current) => current.setApiVersion(value));
	}, []);

	const handleApiKeyChange = React.useCallback((value: string) => {
		setFormData((current) => current.setApiKey(value));
	}, []);

	const handleIsPlatformCredentialChange = React.useCallback((value: boolean) => {
		setFormData((current) => current.setIsPlatformCredential(value));
	}, []);

	const handleSubmit = React.useCallback(() => {
		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		// Unreachable in practice: `AppMain` never mounts this section without an account.
		if (user.account === null) {
			return;
		}

		const accountId = user.account.id;
		const commandData = formData.toCommandData();

		setIsSubmitting(true);

		if (providerId === null) {
			dispatch(
				createAiProvider(
					new CreateAiProvider(
						accountId,
						commandData.label,
						commandData.kind,
						commandData.baseUrl,
						commandData.apiVersion,
						commandData.apiKey,
						commandData.isPlatformCredential,
					),
				),
			)
				.then(() => closeProviderSidebar())
				.catch((action: AiProviderCreationFailedAction) =>
					updateValidationFromExternalSource({
						action,
						eventNames: 'AI_PROVIDER_CREATION_FAILED',
						formDataUpdater: setFormData,
					}),
				)
				.finally(() => setIsSubmitting(false));
			return;
		}

		dispatch(
			updateAiProvider(
				new UpdateAiProvider(
					accountId,
					providerId,
					commandData.label,
					commandData.kind,
					commandData.baseUrl,
					commandData.apiVersion,
					commandData.apiKey === '' ? null : commandData.apiKey,
					commandData.isPlatformCredential,
				),
			),
		)
			.then(() => closeProviderSidebar())
			.catch((action: AiProviderUpdateFailedAction) =>
				updateValidationFromExternalSource({
					action,
					eventNames: 'AI_PROVIDER_UPDATE_FAILED',
					formDataUpdater: setFormData,
				}),
			)
			.finally(() => setIsSubmitting(false));
	}, [formData, providerId, user, dispatch]);

	return (
		<Sidebar
			visible={context !== null}
			onHide={closeProviderSidebar}
			position="right"
			header={providerId === null ? 'Nouveau fournisseur' : 'Modifier le fournisseur'}
			footer={
				<React.Fragment>
					<Button
						type="button"
						severity="secondary"
						label="Annuler"
						onClick={closeProviderSidebar}
					/>
					<Button type="submit" form={formId} label="Enregistrer" loading={isSubmitting} />
				</React.Fragment>
			}
			blockScroll
		>
			<Form
				id={formId}
				onSubmit={handleSubmit}
				aria-label={providerId === null ? 'Déclarer un fournisseur' : 'Modifier un fournisseur'}
			>
				<InputText
					name="label"
					label="Nom du fournisseur"
					required
					value={formData.label}
					onChange={handleLabelChange}
					errorMessage={formData.getValidationMessage('label')}
					isFormFieldValid={formData.isValid('label')}
				/>
				<Dropdown
					name="kind"
					label="Type de fournisseur"
					required
					loading={providerKinds === null}
					options={providerKinds ?? []}
					optionLabel="label"
					optionValue="value"
					value={formData.kind === '' ? null : formData.kind}
					onChange={handleKindChange}
					errorMessage={formData.getValidationMessage('kind')}
					isFormFieldValid={formData.isValid('kind')}
				/>
				<InputText
					name="baseUrl"
					label="URL de base"
					subLabel={
						formData.kind === AZURE_SPEECH_KIND
							? 'Adresse de votre ressource Speech, par exemple https://ma-ressource.cognitiveservices.azure.com. Créez-la dans la région France Central.'
							: 'La racine du service (ex. https://api.openai.com) : le chemin de l’appel est ajouté par le serveur.'
					}
					required
					value={formData.baseUrl}
					onChange={handleBaseUrlChange}
					errorMessage={formData.getValidationMessage('baseUrl')}
					isFormFieldValid={formData.isValid('baseUrl')}
				/>
				{formData.kind === AZURE_OPENAI_KIND ? (
					<InputText
						name="apiVersion"
						label="Version d’API"
						subLabel="Surcharge la version par défaut du serveur (ex. 2025-03-01-preview)."
						required
						value={formData.apiVersion}
						onChange={handleApiVersionChange}
						errorMessage={formData.getValidationMessage('apiVersion')}
						isFormFieldValid={formData.isValid('apiVersion')}
					/>
				) : null}
				<Password
					name="apiKey"
					label="Clé d’API"
					subLabel={
						providerId === null
							? undefined
							: 'Laissez ce champ vide pour conserver la clé déjà enregistrée.'
					}
					required={providerId === null}
					feedback={false}
					toggleMask
					autoComplete="new-password"
					value={formData.apiKey}
					onChange={handleApiKeyChange}
					errorMessage={formData.getValidationMessage('apiKey')}
					isFormFieldValid={formData.isValid('apiKey')}
				/>
				<Switch
					name="isPlatformCredential"
					label="Clé de la plateforme"
					subLabel="Désactivé : clé de Verclam ou du client"
					checked={formData.isPlatformCredential}
					onChange={handleIsPlatformCredentialChange}
				/>
			</Form>
		</Sidebar>
	);
};

export default ProviderSidebar;
